import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Monitor } from 'lucide-react';
import { usePreferences } from '@/contexts/PreferencesContext';

export function DisplayPreferencesCard() {
  const { toast } = useToast();
  const { displayPreferences, loading, updateDisplayPreferences } = usePreferences();
  const [saving, setSaving] = React.useState(false);

  const handleTogglePaymentAdjustment = async (checked: boolean) => {
    setSaving(true);
    try {
      await updateDisplayPreferences({ showPaymentAdjustment: checked });
      toast({
        title: "Saved",
        description: checked 
          ? "Payment adjustment will be shown on the Gap Management page." 
          : "Payment adjustment will be hidden on the Gap Management page.",
      });
    } catch (error) {
      console.error('Error saving display preference:', error);
      toast({
        title: "Save Failed",
        description: "Could not save preference. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Display Options</CardTitle>
          <CardDescription>Loading preferences...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="h-5 w-5" />
          Display Options
        </CardTitle>
        <CardDescription>
          Control what information appears on dashboards and reports.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="show-payment-adjustment" className="text-base font-medium cursor-pointer">
              Show Payment Adjustment
            </Label>
            <p className="text-sm text-muted-foreground">
              Display projected payment adjustments on the Gap Management score card
            </p>
          </div>
          <div className="flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch
              id="show-payment-adjustment"
              checked={displayPreferences.showPaymentAdjustment}
              onCheckedChange={handleTogglePaymentAdjustment}
              disabled={saving}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

